import { SectionCard } from '@/components/ui';
import { useTranslation } from '@/lib/i18n';
import { useOrderFormFieldValuesQuery } from '../hooks/useOrderFormFieldValues';

interface OrderFormFieldValuesSectionProps {
  orderId: string | undefined;
}

/**
 * BO 주문상세 — 주문서 추가정보(통관 등) 값 read-only 섹션. §4 / 15차 D-18[ID144].
 * be-order `GET /api/admin/v1/orders/{orderId}/order-form-field-values` 소비.
 * 항목명 → 값(마스킹 표시값 그대로). 추가정보가 없는 주문(비글로벌 등)은 섹션 자체를
 * 숨겨 빈 카드 노이즈를 줄인다. 값 가공/마스킹은 BE 권위(FE 는 표시만).
 */
export const OrderFormFieldValuesSection = ({
  orderId,
}: OrderFormFieldValuesSectionProps) => {
  const { t } = useTranslation('orders');
  const query = useOrderFormFieldValuesQuery(orderId);

  // 로딩/에러/빈 값이면 섹션 비노출 — 추가정보가 있는 주문에서만 카드를 띄운다.
  // (404·권한 등 에러는 read-only 부가정보라 페이지 본문을 막지 않고 조용히 숨김.)
  const values = query.data?.values ?? [];
  if (query.isPending || query.isError || values.length === 0) return null;

  return (
    <SectionCard
      title={t('orders.detail.orderFormFieldValues', '주문서 추가정보')}
      data-testid="order-form-field-values"
    >
      <ul className="space-y-2" data-sentry-mask>
        {values.map((v) => (
          <li
            key={v.fieldId}
            className="grid grid-cols-[140px_1fr] items-baseline gap-3 text-sm"
          >
            <span className="text-[color:var(--content-muted)]">{v.label}</span>
            <span className="flex items-center gap-2 text-[color:var(--content-strong)]">
              <span>{v.value || '-'}</span>
              {v.masked ? (
                <span
                  className="rounded px-1.5 py-0.5 text-[11px]"
                  style={{
                    background: 'var(--status-neutral-bg)',
                    color: 'var(--status-neutral-fg)',
                  }}
                  title={t('orders.detail.maskedTitle', '개인정보 보호를 위해 마스킹된 값입니다.')}
                >
                  {t('orders.detail.maskedBadge', '마스킹')}
                </span>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
    </SectionCard>
  );
};

/**
 * QA-576 — 주문 상세 왼쪽 요약의 [추가 정보](고도몰 주문 상세 "추가 정보" 위치: 주문자 정보 아래).
 * 중국몰 신분증 번호(身份证号)·위챗(微信) 등 주문서 추가정보를 같은 응답에서 그대로 보여 준다. 없으면 숨긴다.
 */
export const OrderFormFieldValuesInline = ({ orderId }: OrderFormFieldValuesSectionProps) => {
  const { t } = useTranslation('orders');
  const query = useOrderFormFieldValuesQuery(orderId);
  const values = query.data?.values ?? [];
  if (query.isPending || query.isError || values.length === 0) return null;
  return (
    <div className="border-t pt-3" style={{ borderColor: 'var(--surface-1)' }} data-sentry-mask data-testid="order-form-field-values-inline">
      <h3 className="mb-2 text-sm font-semibold text-[color:var(--content-strong)]">
        {t('orders.detail.orderFormFieldValuesInline', '추가 정보')}
      </h3>
      {values.map((v) => (
        <div key={v.fieldId} className="mb-2 grid grid-cols-[110px_minmax(0,1fr)] items-baseline gap-2 text-sm">
          <span className="text-[color:var(--content-muted)] [overflow-wrap:anywhere]">{v.label}</span>
          <span className="min-w-0 text-[color:var(--content-strong)] [overflow-wrap:anywhere]">
            {v.value || '-'}
            {v.masked ? (
              <span
                className="ml-1 rounded px-1.5 py-0.5 text-[11px]"
                style={{ background: 'var(--status-neutral-bg)', color: 'var(--status-neutral-fg)' }}
                title={t('orders.detail.maskedTitle', '개인정보 보호를 위해 마스킹된 값입니다.')}
              >
                {t('orders.detail.maskedBadge', '마스킹')}
              </span>
            ) : null}
          </span>
        </div>
      ))}
    </div>
  );
};
